import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, Plus, Search, Package, Building2, Calendar,
  MoreVertical, Truck, FileText, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const PurchaseOrders = () => {
  const { token, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    vendor_id: '',
    expected_date: '',
    shipping_address: '',
    notes: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, received_quantity: 0, amount: 0 }]
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchOrders();
      fetchVendors();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading, statusFilter]);

  const fetchOrders = async () => {
    try {
      const url = statusFilter === 'all' ? `${API}/purchase-orders` : `${API}/purchase-orders?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/purchase-orders`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase order created');
      setIsDialogOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error('Error creating purchase order');
    }
  };

  const handleReceive = async (poId) => {
    const po = orders.find(o => o.id === poId);
    if (!po) return;

    const receivedItems = po.line_items.map(item => ({
      description: item.description,
      quantity: item.quantity - (item.received_quantity || 0)
    }));

    try {
      await axios.patch(`${API}/purchase-orders/${poId}/receive`, receivedItems, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Items marked as received');
      fetchOrders();
    } catch (error) {
      toast.error('Error receiving items');
    }
  };

  const handleCreateBill = async (poId) => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);

    try {
      await axios.post(`${API}/purchase-orders/${poId}/create-bill?due_date=${dueDate.toISOString().split('T')[0]}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bill created from PO');
      fetchOrders();
    } catch (error) {
      toast.error('Error creating bill');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      expected_date: '',
      shipping_address: '',
      notes: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, received_quantity: 0, amount: 0 }]
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, received_quantity: 0, amount: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    items[index].amount = items[index].quantity * items[index].unit_price;
    setFormData({ ...formData, line_items: items });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-emerald-500/20 text-emerald-400';
      case 'sent': return 'bg-blue-500/20 text-blue-400';
      case 'partially_received': return 'bg-amber-500/20 text-amber-400';
      case 'draft': return 'bg-slate-500/20 text-slate-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const totalValue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const pendingOrders = orders.filter(o => o.status === 'sent' || o.status === 'partially_received').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Purchase Orders</h1>
          <p className="text-slate-400">Manage orders to vendors</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              New PO
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Vendor *</Label>
                  <Select 
                    value={formData.vendor_id}
                    onValueChange={(value) => setFormData({...formData, vendor_id: value})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id} className="text-white">
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Expected Date *</Label>
                  <Input
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData({...formData, expected_date: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Shipping Address</Label>
                <Textarea
                  value={formData.shipping_address}
                  onChange={(e) => setFormData({...formData, shipping_address: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-slate-300">Line Items</Label>
                <div className="space-y-2 mt-2">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="col-span-5 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="col-span-3 bg-slate-800 border-slate-700 text-white"
                      />
                      <div className="col-span-2 text-white text-sm">
                        MYR {item.amount.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                  + Add Line
                </Button>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                <div className="text-lg font-semibold text-white">
                  Total: MYR {formData.line_items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    Create PO
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Orders</p>
                <p className="text-xl font-bold text-white">{orders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Truck className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Pending</p>
                <p className="text-xl font-bold text-white">{pendingOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Package className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Value</p>
                <p className="text-xl font-bold text-white">MYR {totalValue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="draft" className="text-white">Draft</SelectItem>
            <SelectItem value="sent" className="text-white">Sent</SelectItem>
            <SelectItem value="partially_received" className="text-white">Partial</SelectItem>
            <SelectItem value="received" className="text-white">Received</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : orders.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-10 text-center">
            <ShoppingCart className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No purchase orders</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                        <ShoppingCart className="w-6 h-6 text-indigo-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{order.po_number}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {order.vendor_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Expected: {order.expected_date}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {order.currency} {order.total?.toLocaleString()}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-slate-800 border-slate-700">
                          {order.status !== 'received' && order.status !== 'cancelled' && (
                            <DropdownMenuItem 
                              className="text-slate-300"
                              onClick={() => handleReceive(order.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark Received
                            </DropdownMenuItem>
                          )}
                          {(order.status === 'received' || order.status === 'partially_received') && (
                            <DropdownMenuItem 
                              className="text-slate-300"
                              onClick={() => handleCreateBill(order.id)}
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Create Bill
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
